import { useEffect, useRef, useState } from "react";
import { useApp } from "../context/AppContext";
import { Compass as CompassIcon, MapPin, Navigation } from "lucide-react";

const MECCA = { lat: 21.4225, lon: 39.8262 };

function qiblaBearing(lat, lon) {
  const φ1 = (lat * Math.PI) / 180;
  const φ2 = (MECCA.lat * Math.PI) / 180;
  const Δλ = ((MECCA.lon - lon) * Math.PI) / 180;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  const θ = Math.atan2(y, x);
  return (θ * 180) / Math.PI + 360 % 360;
}

export default function Qibla() {
  const { t } = useApp();
  const [coords, setCoords] = useState(null);
  const [bearing, setBearing] = useState(null);
  const [heading, setHeading] = useState(0);
  const [error, setError] = useState(null);
  const orientRef = useRef(null);

  useEffect(() => {
    if (!navigator.geolocation) { setError(t.not_supported); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = { lat: pos.coords.latitude, lon: pos.coords.longitude };
        setCoords(c);
        setBearing(qiblaBearing(c.lat, c.lon));
      },
      () => setError(t.location_error),
      { timeout: 8000 }
    );
  }, [t]);

  useEffect(() => {
    const handler = (e) => {
      const alpha = e.webkitCompassHeading != null ? e.webkitCompassHeading : (360 - (e.alpha || 0));
      setHeading(alpha);
    };
    orientRef.current = handler;
    window.addEventListener("deviceorientationabsolute", handler, true);
    window.addEventListener("deviceorientation", handler, true);
    return () => {
      window.removeEventListener("deviceorientationabsolute", handler, true);
      window.removeEventListener("deviceorientation", handler, true);
    };
  }, []);

  const requestPerm = async () => {
    try {
      if (typeof DeviceOrientationEvent !== "undefined" && typeof DeviceOrientationEvent.requestPermission === "function") {
        await DeviceOrientationEvent.requestPermission();
      }
    } catch (e) {/* ignore */}
  };

  const arrowDeg = bearing != null ? bearing - heading : 0;

  return (
    <div className="space-y-6 fade-in" data-testid="qibla-page">
      <header className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-2xl bg-nour-primary text-white flex items-center justify-center"><CompassIcon className="w-6 h-6" /></div>
        <div>
          <h1 className="font-heading text-2xl font-bold">{t.qibla}</h1>
          <p className="text-sm text-nour-text2 inline-flex items-center gap-1">
            <MapPin className="w-3.5 h-3.5" />
            {coords ? `${coords.lat.toFixed(3)}, ${coords.lon.toFixed(3)}` : "..."}
          </p>
        </div>
      </header>

      {error && <div className="nour-card p-4 text-sm text-red-700">{error}</div>}

      <div className="nour-card p-8 flex flex-col items-center" data-testid="qibla-card">
        <div className="text-sm text-nour-text2 mb-1">{t.direction_to_mecca}</div>
        <div className="text-2xl font-heading font-bold text-nour-primary mb-6">{bearing != null ? Math.round(bearing) + "°" : "—"}</div>

        <div className="relative w-72 h-72 rounded-full bg-emerald-50 border-4 border-emerald-100 flex items-center justify-center">
          {/* North marker */}
          <div className="absolute top-2 text-xs font-bold text-red-500">N</div>
          <div className="absolute bottom-2 text-xs text-nour-text2">S</div>
          <div className="absolute left-3 text-xs text-nour-text2">W</div>
          <div className="absolute right-3 text-xs text-nour-text2">E</div>

          {/* Needle */}
          <div
            className="compass-needle absolute w-1 h-32 origin-bottom"
            style={{ transform: `rotate(${arrowDeg}deg)` }}
          >
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-2xl">🕋</div>
            <div className="w-1 h-32 bg-gradient-to-t from-transparent via-nour-primary to-nour-primary rounded-full"></div>
          </div>
          <div className="w-3 h-3 rounded-full bg-nour-primary z-10"></div>
        </div>

        <button onClick={requestPerm} className="btn-ghost mt-6" data-testid="qibla-calibrate">
          <Navigation className="w-4 h-4" /> {t.qibla_calibrate}
        </button>
      </div>
    </div>
  );
}
